/**
 * What one weather card says, worked out from the two places a briefing can be
 * read from — and from nothing else.
 *
 * There are two, and the difference between them is the whole reason this file
 * is pure and tested. The FETCH (`wxContext` → `WxParsed`) has everything: the
 * decoded observation, the raw bulletins, NOAA's own flight category, the TAF's
 * periods. The STORE has what a plan carries home: the ATIS rows on the form,
 * and `plans.meta.wx` — station ids, observation times, and the neighbour found
 * for an aerodrome that publishes nothing. A shared read-only link and a tab
 * opened before the first fetch lands have only the second one.
 *
 * So a card is built from the fetch where there is one and from the document
 * where there is not, and the two produce the SAME shape. The one thing the
 * document cannot carry is the flight category — `plans.meta.wx` stores times,
 * not weather — so it is derived back out of the decoded cells the form does
 * carry ({@link categoryFromAtis}), through the same
 * {@link flightCategory} the fetch path uses. One table, one set of boundaries,
 * two ways in.
 *
 * Nothing here touches the store, the network or React: `weatherCards` takes
 * values and returns view models, which is what makes the awkward cases —
 * a silent aerodrome whose neighbour is the plan's own departure, a card with a
 * time but no observation — pinned down by `test/weather-model.test.ts` instead
 * of by clicking.
 */
import type { AtisRow } from '@avia/core';

import type { WxNearbyStatus, WxStatus } from '../../store';
import {
  ceilingFt, compassPoint, flightCategory,
  type CompassPoint, type FlightCategory, type NearbyFound, type TafPeriod, type WxParsed,
} from '../../wx';

/* ---------------- times ---------------- */

/**
 * A time as the API or the stored document wrote it → epoch milliseconds.
 *
 * Same three shapes `wx.ts` accepts (ISO-8601, epoch seconds as a number, epoch
 * seconds as digits), because the values here come from both.
 */
export function msOf(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v * 1000 : null;
  if (/^\d{9,}$/.test(v)) {
    const n = Number(v);
    return Number.isFinite(n) ? n * 1000 : null;
  }
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

/** How many whole minutes ago an observation was made; null when it has no time. */
export function ageMin(at: string | number | null | undefined, now: number): number | null {
  const ms = msOf(at);
  if (ms === null) return null;
  return Math.max(0, Math.floor((now - ms) / 60_000));
}

/* ---------------- the category, back out of the decoded cells ---------------- */

/**
 * The ceiling in feet from an ATIS `wolken` cell ("FEW050 BKN100", "NCD", "VV003").
 *
 * The cell is what `parseMetarToAtis` wrote and what the pilot reads on the form,
 * so the groups are always three digits of hundreds of feet. BKN and OVC are a
 * ceiling by definition and FEW/SCT are not; `VV` is an obscured sky, whose
 * vertical visibility IS the ceiling — dropping it would colour a 300 ft fog bank
 * as VFR.
 */
export function ceilingFromClouds(cell: string): number | null {
  const layers: { cover: string; base_ft: number }[] = [];
  for (const m of cell.toUpperCase().matchAll(/\b(FEW|SCT|BKN|OVC|VV)(\d{3})\b/g)) {
    const cover = m[1] === 'VV' ? 'OVX' : (m[1] ?? '');
    layers.push({ cover, base_ft: Number(m[2]) * 100 });
  }
  return ceilingFt(layers);
}

/**
 * The visibility in **statute miles** from an ATIS `sicht` cell.
 *
 * Statute miles because that is the unit {@link flightCategory}'s boundaries are
 * defined in, and this number exists only to be handed to it — nothing is ever
 * shown in SM. The cell says `CAVOK`, `>10 km` or `8 km` (`parseMetarToAtis`),
 * and the first two mean "at least ten", which is above every boundary there is.
 */
const KM_PER_SM = 1.609344;

export function visibSmFromSicht(cell: string): number | null {
  const v = cell.trim().toUpperCase();
  if (v === '') return null;
  if (v === 'CAVOK' || v.startsWith('>')) return 10 / KM_PER_SM;
  const m = v.match(/^(\d+(?:[.,]\d+)?)\s*KM$/);
  if (m) {
    const km = Number((m[1] ?? '').replace(',', '.'));
    return Number.isFinite(km) ? km / KM_PER_SM : null;
  }
  return null;
}

/**
 * The flight category of an observation that survives only as decoded cells.
 *
 * This is the document path: `plans.meta.wx` remembers WHEN a station reported,
 * never WHAT it reported, so a shared plan would show a grey card without this.
 * It goes through the same {@link flightCategory} the fetch path uses, so the
 * two can never disagree about where MVFR starts.
 */
export function categoryFromAtis(
  row: { wolken?: string | undefined; sicht?: string | undefined } | null,
): FlightCategory | null {
  if (!row) return null;
  return flightCategory(ceilingFromClouds(row.wolken ?? ''), visibSmFromSicht(row.sicht ?? ''));
}

/* ---------------- one card ---------------- */

/** Which of the plan's three aerodrome cells a card is about. */
export type CardRole = 'von' | 'nach' | 'alt';

/** The six values a card's body prints, plus the observation time. '' = not reported. */
export interface Decoded {
  zeit: string;
  wind: string;
  sicht: string;
  wolken: string;
  temp: string;
  qnh: string;
  tendenz: string;
}

/** The order the card's definition grid prints them in. */
export const DECODED_ROWS = ['wind', 'sicht', 'wolken', 'temp', 'qnh', 'tendenz'] as const;
export type DecodedRow = (typeof DECODED_ROWS)[number];

const cell = (v: string | undefined): string => (v ?? '').trim();

export function decodedOf(row: AtisRow | Decoded | null | undefined): Decoded | null {
  if (!row) return null;
  const d: Decoded = {
    zeit: cell(row.zeit),
    wind: cell(row.wind),
    sicht: cell(row.sicht),
    wolken: cell(row.wolken),
    temp: cell(row.temp),
    qnh: cell(row.qnh),
    tendenz: cell(row.tendenz),
  };
  return DECODED_ROWS.some(k => d[k] !== '') ? d : null;
}

/** One ATIS row of the form, as it stands. The station cell is the raw cell. */
export interface AtisCells extends Decoded {
  row: number;
  station: string;
}

/**
 * The nearest station that reports, for an aerodrome that does not.
 *
 * `id` and `of` are never collapsed into one field: "LJLJ's report, 24 NM from
 * the LJCE you asked about" is the whole statement, and either half alone is the
 * misreading the fallback exists to prevent.
 */
export interface NearbyCard {
  /** the plan station with no report of its own */
  of: string;
  id: string;
  name: string;
  dist: number;
  dir: CompassPoint;
  category: FlightCategory | null;
  obsTime: string | number | null;
  decoded: Decoded | null;
  metarRaw: string;
  tafRaw: string;
  periods: TafPeriod[];
  /** the ATIS row it was written into; 0 = it is not on the form */
  atisRow: number;
  /** false when the neighbour is one of the plan's own aerodromes — it has its own row */
  takeable: boolean;
}

export interface StationCard {
  role: CardRole;
  id: string;
  /** the aerodrome's name where anything knows one, else '' */
  name: string;
  category: FlightCategory | null;
  obsTime: string | number | null;
  tafValidTo: string | number | null;
  decoded: Decoded | null;
  metarRaw: string;
  tafRaw: string;
  periods: TafPeriod[];
  /** the ATIS row on the form this station's own observation stands in; 0 = none */
  atisRow: number;
  /** this aerodrome publishes no report at all */
  silent: boolean;
  /** the last refresh failed upstream — whatever is here is what the plan remembers */
  unavailable: boolean;
  /**
   * Nothing is known about this aerodrome's weather yet — no values and not even
   * a time. It is what a briefing that has not landed looks like, and it is
   * deliberately NOT the same as {@link silent} or {@link unavailable}: those
   * two are answers.
   */
  pending: boolean;
  nearby: NearbyCard | null;
}

export interface CardsInput {
  /** the plan's three cells, in flight order; entries with no ICAO code are dropped */
  stations: readonly { role: CardRole; id: string }[];
  /** what the last fetch in this session decoded; empty on a shared or freshly opened plan */
  parsed?: readonly WxParsed[] | undefined;
  nearby?: readonly NearbyFound[] | undefined;
  /** what the DOCUMENT remembers (`plans.meta.wx`) */
  status?: WxStatus | null | undefined;
  /** the form's ATIS rows — the only decoded weather a stored plan carries */
  atis?: readonly AtisCells[] | undefined;
  /** aerodrome names by ICAO, from the route */
  names?: Readonly<Record<string, string>> | undefined;
}

/** The first word of a Station/Frequenz cell — "LJLJ ATIS 128.175" names LJLJ. */
const stationOf = (raw: string): string => (raw.trim().split(/\s+/)[0] ?? '').toUpperCase();

/**
 * A neighbour's row identifies itself: `LJLJ (24 NM von LJCE)` is the only thing
 * that ever puts a parenthesis into a station cell, in either language.
 */
const isSubstitute = (raw: string): boolean => raw.includes('(');

const ownRow = (atis: readonly AtisCells[], id: string): AtisCells | null =>
  atis.find(r => stationOf(r.station) === id && !isSubstitute(r.station)) ?? null;

const substituteRow = (atis: readonly AtisCells[], id: string): AtisCells | null =>
  atis.find(r => stationOf(r.station) === id && isSubstitute(r.station)) ?? null;

function nearbyCard(
  of: string,
  found: NearbyFound | null,
  stored: WxNearbyStatus | null,
  atis: readonly AtisCells[],
  names: Readonly<Record<string, string>>,
): NearbyCard | null {
  const id = (found?.id ?? stored?.id ?? '').toUpperCase();
  if (!id) return null;
  const row = substituteRow(atis, id);
  const decoded = decodedOf(found?.parsed.atis ?? null) ?? decodedOf(row);
  return {
    of,
    id,
    name: found?.name || stored?.name || names[id] || '',
    dist: Math.round(found?.distNm ?? stored?.dist_nm ?? 0),
    dir: found ? found.dir : compassPoint(stored?.bearing_deg ?? 0),
    category: found?.parsed.category ?? categoryFromAtis(decoded),
    obsTime: found?.parsed.obsTime ?? stored?.obs_time ?? null,
    decoded,
    metarRaw: found?.parsed.metarRaw ?? '',
    tafRaw: found?.parsed.tafRaw ?? '',
    periods: found?.parsed.periods ?? [],
    atisRow: row?.row ?? 0,
    takeable: found?.takeable ?? true,
  };
}

/**
 * One card per aerodrome the plan names, in flight order.
 *
 * A cell with no location indicator produces no card — `von` may well say
 * "Platzrunde", and a card headed by a blank is worse than one card fewer.
 */
export function weatherCards(input: CardsInput): StationCard[] {
  const parsed = input.parsed ?? [];
  const nearby = input.nearby ?? [];
  const atis = input.atis ?? [];
  const names = input.names ?? {};
  const status = input.status ?? null;

  const out: StationCard[] = [];
  for (const { role, id: raw } of input.stations) {
    const id = raw.trim().toUpperCase();
    if (!id) continue;

    const live = parsed.find(p => p.id === id) ?? null;
    const stored = status?.stations.find(s => (s.id ?? '').toUpperCase() === id) ?? null;
    const own = ownRow(atis, id);
    const decoded = decodedOf(live?.atis ?? null) ?? decodedOf(own);

    const found = nearby.find(n => n.for === id) ?? null;
    const storedNear = status?.nearby?.find(n => (n.for ?? '').toUpperCase() === id) ?? null;
    const near = nearbyCard(id, found, storedNear, atis, names);

    const unavailable = live?.unavailable === true;
    /* "silent" is a statement about the AERODROME, not about this session: a
       fetch that came back without a METAR says it, and so does a fallback the
       document remembers from a fetch in another tab. */
    const silent = (live !== null && live.atis === null && !unavailable) || near !== null;
    const obsTime = live?.obsTime ?? stored?.obs_time ?? null;
    /* Nothing is known about this aerodrome's weather — not the values, not even
       when it last reported. That is a station whose briefing has not landed
       yet, and it is NOT the same as one that publishes nothing (`silent`) or
       one whose refresh failed (`unavailable`): those two have an answer. */
    const pending = decoded === null && obsTime === null && !silent && !unavailable;

    out.push({
      role,
      id,
      name: names[id] ?? '',
      category: live?.category ?? categoryFromAtis(decoded),
      obsTime,
      tafValidTo: live?.tafValidTo ?? stored?.taf_valid_to ?? null,
      decoded,
      metarRaw: live?.metarRaw ?? '',
      tafRaw: live?.tafRaw ?? '',
      periods: live?.periods ?? [],
      atisRow: own?.row ?? 0,
      silent,
      unavailable,
      pending,
      nearby: near,
    });
  }
  return out;
}
